<template>
  <div class="mb-10 rounded-lg" style="box-shadow: 0 4px 10px rgba(0,0,0,0.06)">
    <div class='py-7 px-4 text-body-1 font-weight-bold grey lighten-4'>
      <v-icon class="mr-2">mdi-arrow-all</v-icon>
      {{ 'notBookedRequestsAndQuotedPriceTargetPrice'|localize }}
    </div>
    <template v-if='hasCompanyContext'>
      <v-data-table
        :headers="headers"
        :items="requestList"
        :loading="loading"
        loader-height="2"
        :options.sync="options"
        :server-items-length="totalItems"
        hide-default-footer
      >
        <template v-slot:no-data>
          <EmptyState icon="mdi-inbox-outline" :title="'emptyGenericTitle'|localize" :subtitle="'emptyGenericSubtitle'|localize"/>
        </template>
        <template v-slot:header.road="{ header }">
          <div class='d-flex justify-center align-center flex-column'>
            <div>
              <v-icon>mdi-truck</v-icon> {{ header.text }}
            </div>
            <div class='d-flex align-center'>
              <div class='pr-1'>{{ 'absolute'|localize }}</div>
              <div class='pl-1'>{{ 'deviation'|localize }}</div>
            </div>
          </div>
        </template>
        <template v-slot:header.sea="{ header }">
          <div class='d-flex justify-center align-center flex-column'>
            <div>
              <v-icon>mdi-ferry</v-icon> {{ header.text }}
            </div>
            <div class='d-flex align-center'>
              <div class='pr-1'>{{ 'absolute'|localize }}</div>
              <div class='pl-1'>{{ 'deviation'|localize }}</div>
            </div>
          </div>
        </template>
        <template v-slot:header.air="{ header }">
          <div class='d-flex justify-center align-center flex-column'>
            <div>
              <v-icon>mdi-airplane</v-icon> {{ header.text }}
            </div>
            <div class='d-flex align-center'>
              <div class='pr-1'>{{ 'absolute'|localize }}</div>
              <div class='pl-1'>{{ 'deviation'|localize }}</div>
            </div>
          </div>
        </template>
        <template v-slot:header.rail="{ header }">
          <div class='d-flex justify-center align-center flex-column'>
            <div>
              <v-icon>mdi-train</v-icon> {{ header.text }}
            </div>
            <div class='d-flex align-center'>
              <div class='pr-1'>{{ 'absolute'|localize }}</div>
              <div class='pl-1'>{{ 'deviation'|localize }}</div>
            </div>
          </div>
        </template>
        <template v-slot:item.request_id="{ item }">
          <router-link class="black--text" target='_blank' style="vertical-align: middle" :to="requestDetailsRoute(item)">
            {{ 'request' | localize }} ID <strong>{{ item.request_id }}</strong>
          </router-link>
        </template>
        <template v-slot:item.road="{ item }">
          <div v-if="item.transport_mode === TRANSPORT_TYPES.ROAD">
            <strong>{{ item.quoted_price }}/</strong><strong>{{ item.target_price }}</strong>
            <strong class='ml-2'>{{ sign(item.percent) }}{{ item.percent }}%</strong>
          </div>
        </template>
        <template v-slot:item.sea="{ item }">
          <div v-if="item.transport_mode === TRANSPORT_TYPES.SEA">
            <strong>{{ item.quoted_price }}/</strong><strong>{{ item.target_price }}</strong>
            <strong class='ml-2'>{{ sign(item.percent) }}{{ item.percent }}%</strong>
          </div>
        </template>
        <template v-slot:item.air="{ item }">
          <div v-if="item.transport_mode === TRANSPORT_TYPES.AIR">
            <strong>{{ item.quoted_price }}/</strong><strong>{{ item.target_price }}</strong>
            <strong class='ml-2'>{{ sign(item.percent) }}{{ item.percent }}%</strong>
          </div>
        </template>
        <template v-slot:item.rail="{ item }">
          <div v-if="item.transport_mode === TRANSPORT_TYPES.RAIL">
            <strong>{{ item.quoted_price }}/</strong><strong>{{ item.target_price }}</strong>
            <strong class='ml-2'>{{ sign(item.percent) }}{{ item.percent }}%</strong>
          </div>
        </template>
      </v-data-table>
      <v-pagination
        color="primary"
        v-model="currentPage"
        :length="totalPages"
        :total-visible="7"
        class="py-6"
        :disabled="loading"
        @input="requestList = [];getRequests();"
      />
    </template>
    <div v-else class="pa-3">
      <v-alert class="my-0 text-center" dense outlined text color='grey lighten-1'>{{ "transportCompanyIsNotSelected"|localize }}</v-alert>
    </div>
  </div>
</template>

<script>
  import {TRANSPORT_TYPES} from '@/enums/transportMode';
  import api from '@/api/api';
  import localize from '@/filters/localize';
  import EmptyState from '@/components/common/EmptyState';

  export default {
    name: 'QuotedAboveTarget',
    components: {EmptyState},

    props: ['companies', 'companiesLoading', 'filters', 'companyScoped'],

    data() {
      return {
        loading: false,
        defaultSortBy: '',
        defaultSortDesc: true,
        options: {},
        itemsPerPage: 20,
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        requestList: [],
      };
    },

    mounted() {
      if (this.companyScoped) {
        this.getRequests();
      }
    },

    computed: {
      TRANSPORT_TYPES() {
        return TRANSPORT_TYPES;
      },
      activeCompanyId() {
        return this.$store.getters.userData?.company?.id || null;
      },
      hasCompanyContext() {
        return this.companyScoped ? this.activeCompanyId !== null : this.filters.company !== null;
      },
      headers() {
        return [
          {value: 'request_id', sortable: false, width: '20%'},
          {text: localize('road'), sortable: false, align: 'center', value: 'road', width: '20%'},
          {text: localize('sea'), sortable: false, align: 'center', value: 'sea', width: '20%'},
          {text: localize('air'), sortable: false, align: 'center', value: 'air', width: '20%'},
          {text: localize('rail'), sortable: false, align: 'center', value: 'rail', width: '20%'},
        ];
      },
    },

    methods: {
      requestDetailsRoute(item) {
        return this.companyScoped
          ? {name: 'company-request-details', query: {request_id: item.request_id, mode: item.transport_mode, report: 1}}
          : {name: 'admin-request-details', query: {request_id: item.request_id}};
      },
      sign(value) {
        if (value > 0) return '+'
        if (value < 0) return ''
        return '±'
      },
      clearRequestList() {
        this.requestList = [];
      },
      queryData() {
        let queryData = {};
        queryData['per_page'] = this.itemsPerPage;
        queryData['page'] = this.currentPage;
        queryData.filters = {};
        if (this.filters.dateFrom || this.filters.dateTo) {
          queryData.filters.date = ["", ""];
          this.filters.dateFrom && (queryData.filters.date[0] = this.filters.dateFrom);
          this.filters.dateTo && (queryData.filters.date[1] = this.filters.dateTo);
        }
        if (this.companyScoped) {
          queryData.company_id = this.activeCompanyId;
        } else if (this.filters.company) {
          queryData.filters.company = [this.filters.company];
        }
        this.filters.country_from.length && (queryData.filters.country_from = this.filters.country_from);
        this.filters.country_to.length && (queryData.filters.country_to = this.filters.country_to);
        return queryData;
      },
      async getRequests() {
        if (!this.hasCompanyContext) {
          this.clearRequestList();
          return
        }
        try {
          this.loading = true;
          const performanceResponse = this.companyScoped
            ? await api.getCompanyNotBookedRequestsOverTarget(this.queryData())
            : await api.getNotBookedRequestsOverTarget(this.queryData());
          this.setRequestsData(performanceResponse);
        } catch (e) {} finally {
          this.loading = false;
        }
      },
      setRequestsData(requestsData) {
        this.requestList = requestsData?.data?.data || [];
        this.totalPages = requestsData.data.pagination['total_page'];
        this.totalItems = requestsData.data.pagination['total_items'];
      },
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          this.currentPage = 1;
          this.clearRequestList();
          this.getRequests();
        },
      },
    },
  };
</script>
